import React from 'react';
import { useImpressionRef } from '@noths/polaris-client-react-hooks';
import { SvgIcon } from '@noths/polaris-client-ribbons-design-system';
import { priceFormatter } from '@noths/polaris-client-utils';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import { getIconPath } from 'src/utils/assetPaths';
import * as styles from './ProductFeatures.style';

export enum ProductFeature {
  FreeGiftWrappingAvailable,
  GiftWrappingAvailable,
  PhotoPersonalisation,
  Personalisable,
  MadeInBritain,
  LoveByCustomers,
}

interface IconLabel {
  icon: string;
  label: string;
}

interface GetFeatureIconAndLabelOptions {
  currency: CurrencyCode;
  minimumPersonalisationCost: number;
}

const getFeatureIconAndLabel = (
  feature: ProductFeature,
  { currency, minimumPersonalisationCost }: GetFeatureIconAndLabelOptions,
) => {
  switch (feature) {
    case ProductFeature.GiftWrappingAvailable:
      return {
        icon: getIconPath('icons-24-gift.svg'),
        label: 'Gift wrapping available',
      };
    case ProductFeature.FreeGiftWrappingAvailable:
      return {
        icon: getIconPath('icons-24-gift.svg'),
        label: 'Free gift wrapping available',
      };
    case ProductFeature.PhotoPersonalisation:
      return {
        icon: getIconPath('icons-16-photo.svg'),
        label: 'Photo personalisation',
      };
    case ProductFeature.Personalisable:
      return {
        icon: getIconPath('icons-24-mode-edit.svg'),
        label:
          minimumPersonalisationCost > 0
            ? `Can be personalised from ${priceFormatter(currency, minimumPersonalisationCost)}`
            : 'Can be personalised',
      };
    case ProductFeature.MadeInBritain:
      return {
        icon: getIconPath('icons-24-location-pin.svg'),
        label: 'Made with care in Britain',
      };
    case ProductFeature.LoveByCustomers:
      return {
        icon: getIconPath('icons-16-star-fill.svg'),
        label: 'Our customers love it',
      };
    default:
      return null;
  }
};

export interface ProductFeaturesProps {
  currency: CurrencyCode;
  minimumPersonalisationCost?: number;
  onVisible?: (productFeaturesLabels: string) => void;
  productFeatures: ProductFeature[];
}

export const ProductFeatures = ({
  currency,
  minimumPersonalisationCost = 0,
  onVisible,
  productFeatures,
}: ProductFeaturesProps) => {
  const iconLabelPairs: IconLabel[] = productFeatures
    .map((feature) => getFeatureIconAndLabel(feature, { currency, minimumPersonalisationCost }))
    .filter((pair): pair is IconLabel => pair !== null);

  const productFeaturesLabels = iconLabelPairs.map((pair) => pair.label).join(', ');
  const productFeaturesRef = useImpressionRef<HTMLUListElement>(() =>
    onVisible?.(productFeaturesLabels),
  );

  return (
    <ul css={styles.list} ref={productFeaturesRef}>
      {iconLabelPairs.map(({ icon, label }) => {
        return (
          <li css={styles.listItem} key={label}>
            <SvgIcon color={'var(--brand-purple-base)'} height={24} src={icon} width={24} />
            <span css={styles.text}>{label}</span>
          </li>
        );
      })}
    </ul>
  );
};
